import { useEffect, useMemo, useState } from 'react';
import {
  X,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus as MinusIcon,
  Calendar as CalendarIcon,
  Loader2,
  Cat,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { useClassStore } from '../store/useClassStore';
import { usePetStore } from '../store/usePetStore';
import { getPetImage, getPetName } from '../lib/petUtils';
import CosImage from './CosImage';

// ---------- Types ----------

interface BreakdownEntry {
  reason: string;
  count: number;
  total: number;
}

interface RedemptionEntry {
  goodName: string;
  cost: number;
  date: string;
}

interface PeriodSummary {
  gained: number;
  lost: number;
  net: number;
  topBonus: BreakdownEntry[];
  topDeduction: BreakdownEntry[];
  checkin: {
    completed: number;
    possible: number;
    rate: number;
    byDay: number[];
  };
  redemptions: RedemptionEntry[];
}

interface PreviousSummary {
  gained: number;
  lost: number;
  net: number;
  checkinRate: number;
}

interface StudentReport {
  id: number;
  name: string;
  petType: string | null;
  currentPoints: number;
  availablePoints: number;
  unlockedPetsCount: number;
  current: PeriodSummary;
  previous: PreviousSummary;
}

interface ReportSummary {
  period: {
    start: string;
    end: string;
    days: number;
    previousStart: string;
    previousEnd: string;
  };
  students: StudentReport[];
}

interface ReportsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Preset = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'custom';

// ---------- Date helpers (Monday = first day of week) ----------

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Mon-based week index: Mon=0..Sun=6
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

function startOfWeekMon(d: Date): Date {
  const base = startOfDay(d);
  base.setDate(base.getDate() - mondayIndex(base));
  return base;
}

function endOfWeekMon(d: Date): Date {
  const start = startOfWeekMon(d);
  start.setDate(start.getDate() + 6);
  return start;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function rangeForPreset(preset: Preset, today: Date): { start: string; end: string } {
  switch (preset) {
    case 'thisWeek': {
      return { start: toDateKey(startOfWeekMon(today)), end: toDateKey(endOfWeekMon(today)) };
    }
    case 'lastWeek': {
      const lastWeekRef = new Date(today);
      lastWeekRef.setDate(lastWeekRef.getDate() - 7);
      return { start: toDateKey(startOfWeekMon(lastWeekRef)), end: toDateKey(endOfWeekMon(lastWeekRef)) };
    }
    case 'thisMonth': {
      return { start: toDateKey(startOfMonth(today)), end: toDateKey(endOfMonth(today)) };
    }
    case 'lastMonth': {
      const lastMonthRef = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return { start: toDateKey(startOfMonth(lastMonthRef)), end: toDateKey(endOfMonth(lastMonthRef)) };
    }
    default:
      return { start: toDateKey(startOfWeekMon(today)), end: toDateKey(endOfWeekMon(today)) };
  }
}

// ---------- Component ----------

export default function ReportsModal({ isOpen, onClose }: ReportsModalProps) {
  const { t } = useTranslation();
  const { currentClass } = useClassStore();
  const { petConfig } = usePetStore();

  const [preset, setPreset] = useState<Preset>('thisWeek');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState<ReportSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize date range when modal opens
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const range = rangeForPreset('thisWeek', today);
      setPreset('thisWeek');
      setStartDate(range.start);
      setEndDate(range.end);
      setData(null);
      setError(null);
    }
  }, [isOpen]);

  // Re-fetch whenever the active range changes (and modal is open)
  useEffect(() => {
    if (!isOpen || !currentClass || !startDate || !endDate) return;
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/reports/summary', {
          params: {
            classId: currentClass.id,
            startDate,
            endDate,
          },
        });
        if (!cancelled) setData(res.data);
      } catch (e: unknown) {
        if (!cancelled) {
          console.error('Failed to fetch report', e);
          setError(t('reports.toast.fetchFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentClass?.id, startDate, endDate, t]);

  const handlePresetClick = (p: Preset) => {
    if (p === 'custom') {
      setPreset('custom');
      return;
    }
    const range = rangeForPreset(p, new Date());
    setPreset(p);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleStartChange = (v: string) => {
    setPreset('custom');
    setStartDate(v);
  };

  const handleEndChange = (v: string) => {
    setPreset('custom');
    setEndDate(v);
  };

  const periodLabel = useMemo(() => {
    if (!startDate || !endDate) return '';
    return startDate === endDate ? startDate : `${startDate} ~ ${endDate}`;
  }, [startDate, endDate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start md:items-center justify-center z-[100] p-2 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[calc(100vh-1rem)] md:h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">{t('reports.title')}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{periodLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Period selector */}
        <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-white shrink-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 bg-amber-50/70 rounded-2xl p-1">
              {([
                { id: 'thisWeek', label: t('reports.preset.thisWeek') },
                { id: 'lastWeek', label: t('reports.preset.lastWeek') },
                { id: 'thisMonth', label: t('reports.preset.thisMonth') },
                { id: 'lastMonth', label: t('reports.preset.lastMonth') },
              ] as const).map((p) => {
                const active = preset === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => handlePresetClick(p.id)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                      active
                        ? 'bg-white text-amber-600 shadow-sm shadow-amber-100/60'
                        : 'text-gray-500 hover:text-amber-600'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleStartChange(e.target.value)}
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs outline-none focus:border-amber-400 transition-colors"
              />
              <span className="text-gray-400 text-xs">~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleEndChange(e.target.value)}
                className="px-2 py-1 border border-gray-200 rounded-lg text-xs outline-none focus:border-amber-400 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto bg-gray-50/30">
          <div className="p-4 sm:p-6 space-y-4 bg-gray-50/30">
            {loading && (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                <span className="text-sm">{t('common.loading')}</span>
              </div>
            )}
            {!loading && error && (
              <div className="text-center text-rose-500 py-20 text-sm">{error}</div>
            )}
            {!loading && !error && data && data.students.length === 0 && (
              <div className="text-center text-gray-400 py-20 text-sm">{t('reports.empty')}</div>
            )}
            {!loading && !error && data && data.students.length > 0 && (
              <>
                {data.students.map((student) => (
                  <StudentReportCard
                    key={student.id}
                    student={student}
                    petConfig={petConfig}
                    daysInRange={data.period.days}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Student card sub-component ----------

interface StudentReportCardProps {
  student: StudentReport;
  petConfig: any[];
  daysInRange: number;
}

function StudentReportCard({ student, petConfig, daysInRange }: StudentReportCardProps) {
  const { t } = useTranslation();

  // Net delta vs previous period
  const netDelta = student.current.net - student.previous.net;
  const netDeltaIcon =
    netDelta > 0 ? (
      <TrendingUp className="w-3 h-3" />
    ) : netDelta < 0 ? (
      <TrendingDown className="w-3 h-3" />
    ) : (
      <MinusIcon className="w-3 h-3" />
    );
  const netDeltaCls =
    netDelta > 0
      ? 'bg-emerald-100 text-emerald-600'
      : netDelta < 0
        ? 'bg-rose-100 text-rose-600'
        : 'bg-gray-100 text-gray-500';

  // Bar widths: gained vs lost relative to the larger
  const barMax = Math.max(student.current.gained, student.current.lost, 1);
  const gainedPct = (student.current.gained / barMax) * 100;
  const lostPct = (student.current.lost / barMax) * 100;

  // Checkin rate
  const ratePct = Math.round(student.current.checkin.rate * 100);
  const prevRatePct = Math.round(student.previous.checkinRate * 100);
  const rateDelta = ratePct - prevRatePct;

  // 7-day mini strip color intensity (clamped 0..3+)
  const maxByDay = Math.max(1, ...student.current.checkin.byDay);

  // Pet image (level 1 fallback if no level info)
  const petImage = student.petType ? getPetImage(student.petType, 1, petConfig) : null;

  return (
    <div className="bg-white rounded-clay border border-gray-100 shadow-clay-sm hover:shadow-clay transition-shadow duration-200 p-4 sm:p-5">
      {/* Header row */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-50">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-50 to-purple-50 border border-gray-100 flex items-center justify-center overflow-hidden shrink-0">
          {petImage ? (
            <CosImage src={petImage} alt={getPetName(student.petType || '')} className="w-full h-full object-contain" />
          ) : (
            <span className="text-2xl">🥚</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-base sm:text-lg font-black text-gray-800 truncate">{student.name}</h3>
            {student.petType && (
              <span
                data-export-pill="pet"
                className="text-[10px] font-bold bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-md px-1.5 py-0.5 flex items-center gap-0.5 shrink-0"
              >
                <Cat className="w-3 h-3" />
                {getPetName(student.petType)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>
              {t('reports.card.totalPoints')} <span className="font-bold text-gray-700">{student.currentPoints}</span>
            </span>
            <span className="opacity-40">·</span>
            <span>
              {t('reports.card.availablePoints')} <span className="font-bold text-amber-600">{student.availablePoints}</span>
            </span>
          </div>
        </div>
        <div
          data-export-pill="netDelta"
          className={`px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 shrink-0 ${netDeltaCls}`}
        >
          {netDeltaIcon}
          {netDelta > 0 ? '+' : ''}
          {netDelta}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Left column: net + bars + top bonus/deduction */}
        <div className="space-y-3">
          {/* Net summary */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-emerald-50/60 rounded-lg py-2">
              <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider">{t('reports.card.gained')}</div>
              <div className="text-lg font-black text-emerald-600">+{student.current.gained}</div>
            </div>
            <div className="bg-rose-50/60 rounded-lg py-2">
              <div className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">{t('reports.card.lost')}</div>
              <div className="text-lg font-black text-rose-600">-{student.current.lost}</div>
            </div>
            <div className="bg-amber-50/60 rounded-lg py-2">
              <div className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">{t('reports.card.net')}</div>
              <div className={`text-lg font-black ${student.current.net >= 0 ? 'text-amber-600' : 'text-rose-600'}`}>
                {student.current.net >= 0 ? '+' : ''}
                {student.current.net}
              </div>
            </div>
          </div>

          {/* Bars */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 w-8 text-right">{t('reports.card.gainedShort')}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
                  style={{ width: `${gainedPct}%` }}
                />
              </div>
              <span className="text-[10px] font-bold text-gray-500 w-8">{student.current.gained}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 w-8 text-right">{t('reports.card.lostShort')}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-rose-400 to-rose-500 rounded-full"
                  style={{ width: `${lostPct}%` }}
                />
              </div>
              <span className="text-[10px] font-bold text-gray-500 w-8">{student.current.lost}</span>
            </div>
          </div>

          {/* Top bonus */}
          {student.current.topBonus.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider mb-1">
                {t('reports.card.topBonus')}
              </div>
              <div className="space-y-1">
                {student.current.topBonus.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 truncate flex-1 mr-2">{b.reason}</span>
                    <span className="text-gray-400 mr-2">×{b.count}</span>
                    <span className="font-bold text-emerald-600">+{b.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top deduction */}
          {student.current.topDeduction.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-rose-500 uppercase tracking-wider mb-1">
                {t('reports.card.topDeduction')}
              </div>
              <div className="space-y-1">
                {student.current.topDeduction.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 truncate flex-1 mr-2">{b.reason}</span>
                    <span className="text-gray-400 mr-2">×{b.count}</span>
                    <span className="font-bold text-rose-600">-{b.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column: checkin + redemptions */}
        <div className="space-y-3">
          {/* Check-in rate + per-day strip */}
          <div className="bg-sky-50/40 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold text-sky-500 uppercase tracking-wider">
                {t('reports.card.checkinRate')}
              </div>
              {student.current.checkin.possible > 0 && rateDelta !== 0 && (
                <div className={`text-[10px] font-bold ${rateDelta > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {rateDelta > 0 ? '+' : ''}
                  {rateDelta}%
                </div>
              )}
            </div>
            <div className="flex items-baseline gap-1.5 mb-2">
              <span className="text-2xl font-black text-sky-600">{ratePct}%</span>
              <span className="text-xs text-gray-400">
                ({student.current.checkin.completed}/{student.current.checkin.possible})
              </span>
            </div>
            {/* Per-day strip — show up to 14 days; collapse longer */}
            {student.current.checkin.byDay.length > 0 && daysInRange <= 31 && (
              <div className="flex gap-0.5 flex-wrap">
                {student.current.checkin.byDay.map((c, i) => {
                  const intensity = c === 0 ? 0 : Math.min(4, Math.ceil((c / maxByDay) * 4));
                  const bg = ['bg-gray-100', 'bg-sky-200', 'bg-sky-300', 'bg-sky-400', 'bg-sky-500'][intensity];
                  return (
                    <div
                      key={i}
                      className={`${bg} rounded-sm flex-1 min-w-[6px]`}
                      style={{ height: 12 }}
                      title={`+${c}`}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Redemptions */}
          <div>
            <div className="text-[10px] font-bold text-amber-500 uppercase tracking-wider mb-1">
              {t('reports.card.redemptions')}
            </div>
            {student.current.redemptions.length === 0 ? (
              <div className="text-xs text-gray-400">{t('reports.card.noRedemptions')}</div>
            ) : (
              <div className="space-y-1">
                {student.current.redemptions.map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700 truncate flex-1 mr-2">{r.goodName}</span>
                    <span className="text-gray-400 mr-2">{r.date.slice(5)}</span>
                    <span className="font-bold text-amber-600">-{r.cost}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
